import type { KeyboardEventHandler } from 'react';
import { useCallback } from 'react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterParts } from '@entities/dadata';

import {
  CheckboxController,
  DadataFioController,
  FormControlContainer,
  KeyCode,
  TwoColumnContainer,
} from '@shared/index';

import type { FioFieldsType } from '../model';

interface FioFieldsProps {
  testId: string;
}

export const FioFields = ({ testId }: FioFieldsProps) => {
  const { t } = useTranslation();
  const { control, clearErrors, setValue, watch } =
    useFormContext<FioFieldsType>();

  const middleNameNotRequired = watch('noMiddleName');

  const handleKeyDown = useCallback(
    (inputIndex: number): KeyboardEventHandler<HTMLInputElement> =>
      (e) => {
        e.keyCode === KeyCode.ENTER &&
          document.getElementsByTagName('input')[inputIndex].focus();
      },
    []
  );

  const handleFioChange = useCallback(
    (name: keyof FioFieldsType) => (value: string) => {
      clearErrors();
      setValue(name, value);
    },
    []
  );

  const handleCheckboxChange = useCallback(() => {
    clearErrors('middleName');
    setValue('middleName', '');
  }, []);

  return (
    <>
      <TwoColumnContainer $marginBottom={8}>
        <DadataFioController<FioFieldsType>
          control={control}
          name='lastName'
          filterParts={[FilterParts.SURNAME]}
          id='dadata-lastName'
          label={t('REGISTRATION:labels.lastName') || ''}
          onChange={handleFioChange('lastName')}
          onKeyDown={handleKeyDown(1)}
          testId={`${testId}.lastName`}
        />
        <DadataFioController<FioFieldsType>
          control={control}
          name='firstName'
          filterParts={[FilterParts.NAME]}
          label={t('REGISTRATION:labels.firstName') || ''}
          id='dadata-firstName'
          onChange={handleFioChange('firstName')}
          onKeyDown={handleKeyDown(1)}
          testId={`${testId}.firstName`}
        />
      </TwoColumnContainer>
      <TwoColumnContainer $marginBottom={8}>
        <DadataFioController<FioFieldsType>
          control={control}
          name='middleName'
          id='dadata-middleName'
          label={t('REGISTRATION:labels.middleName') || ''}
          filterParts={[FilterParts.PATRONYMIC]}
          onChange={handleFioChange('middleName')}
          onKeyDown={handleKeyDown(4)}
          disabled={middleNameNotRequired}
          testId={`${testId}.middleName`}
        />
      </TwoColumnContainer>
      <FormControlContainer>
        <CheckboxController<FioFieldsType>
          control={control}
          name='noMiddleName'
          label={t('REGISTRATION:labels.noMiddleName') || ''}
          onChange={handleCheckboxChange}
          testId={`${testId}.noMiddleName`}
        />
      </FormControlContainer>
    </>
  );
};
